// RichdownComposerFocusRouter.js
// Manages focus routing between the chat and agent composers
import React, { createContext, useCallback, useContext, useEffect, useRef } from 'react';

import { isEditorViewMounted } from '../lib/tiptapEditor';
import { KEYCHAIN, useKeyMaster } from './KeyMaster';

const RichdownComposerFocusRouterContext = createContext(null);

// Valid slot names
const SLOTS = {
  CHANNEL: 'channel',
  AGENT_CHAT: 'agent_chat',
};

// Channel is always the default slot for keystroke routing
const DEFAULT_SLOT = SLOTS.CHANNEL;

export function RichdownComposerFocusRouterProvider({ children }) {
  const { register: kmRegister, unregister: kmUnregister, unlocked } = useKeyMaster();

  // Explicit slots for chat and agent composers
  const slotsRef = useRef({
    [SLOTS.CHANNEL]: { editor: null, ref: null },
    [SLOTS.AGENT_CHAT]: { editor: null, ref: null },
  });

  // Which composer was last actively used (for keystroke routing)
  const activeSlotRef = useRef(DEFAULT_SLOT);

  // For visibility restoration - which slot had focus when hidden
  const focusTargetBeforeHideSlotRef = useRef(null);

  // Helper functions
  const isInputElement = useCallback((element) => {
    return (
      element &&
      (element.tagName === 'INPUT' || element.tagName === 'TEXTAREA' || element.tagName === 'SELECT' || element.isContentEditable)
    );
  }, []);

  const isTypingKey = useCallback((event) => {
    return (
      (event.key.length === 1 && !event.ctrlKey && !event.metaKey && !event.altKey) ||
      event.key === 'Enter' ||
      event.key === 'Backspace' ||
      event.key === 'Delete'
    );
  }, []);

  const isTypableKey = useCallback((key) => {
    return key.length === 1;
  }, []);

  const isValidSlot = useCallback((slot) => {
    return slot === SLOTS.CHANNEL || slot === SLOTS.AGENT_CHAT;
  }, []);

  const isValidFocusTarget = useCallback((editor) => {
    return isEditorViewMounted(editor) && !!editor?.options?.element && document.body.contains(editor.options.element);
  }, []);

  // Get the editor for a slot, checking validity
  const getSlotEditor = useCallback(
    (slot) => {
      const slotData = slotsRef.current[slot];
      if (slotData?.editor && isValidFocusTarget(slotData.editor)) {
        return slotData;
      }
      return null;
    },
    [isValidFocusTarget],
  );

  // When a modal owns keyboard priority and embeds its own composer, route typing
  // into that composer instead of falling back to the hidden channel composer.
  // Returns null when no modal override applies (or the modal composer isn't registered yet).
  const getModalOverrideEditor = useCallback(() => {
    const ownsKeyboard = unlocked(KEYCHAIN.MODAL__CREATE_WIDGET) || unlocked(KEYCHAIN.MODAL__WIDGET_COMPOSER);
    if (!ownsKeyboard) return null;
    const agentEditor = getSlotEditor(SLOTS.AGENT_CHAT);
    if (!agentEditor) return null;
    return { slot: SLOTS.AGENT_CHAT, ...agentEditor };
  }, [unlocked, getSlotEditor]);

  // Get the active editor, with fallback to default slot
  const getActiveEditor = useCallback(() => {
    const override = getModalOverrideEditor();
    if (override) return override;

    // First try the active slot
    const activeSlot = activeSlotRef.current;
    if (activeSlot) {
      const active = getSlotEditor(activeSlot);
      if (active) {
        return { slot: activeSlot, ...active };
      }
    }

    // Fallback to chat (the default)
    const chatEditor = getSlotEditor(SLOTS.CHANNEL);
    if (chatEditor) {
      return { slot: SLOTS.CHANNEL, ...chatEditor };
    }

    // Fallback to agent
    const agentEditor = getSlotEditor(SLOTS.AGENT_CHAT);
    if (agentEditor) {
      return { slot: SLOTS.AGENT_CHAT, ...agentEditor };
    }

    return null;
  }, [getSlotEditor, getModalOverrideEditor]);

  // Get the focus target for visibility restore
  const getFocusTarget = useCallback(() => {
    const override = getModalOverrideEditor();
    if (override) return override;

    // First priority: restore to slot that had focus before hide
    const beforeHideSlot = focusTargetBeforeHideSlotRef.current;
    if (beforeHideSlot) {
      const beforeHide = getSlotEditor(beforeHideSlot);
      if (beforeHide) {
        return { slot: beforeHideSlot, ...beforeHide };
      }
    }

    // Second: active editor
    return getActiveEditor();
  }, [getSlotEditor, getActiveEditor, getModalOverrideEditor]);

  const focusBestTarget = useCallback(() => {
    const target = getFocusTarget();
    if (target?.editor) {
      target.editor.commands.focus();
      focusTargetBeforeHideSlotRef.current = null;
    }
  }, [getFocusTarget]);

  // Registration functions
  const register = useCallback(
    (slot, editor, ref) => {
      if (!isValidSlot(slot)) {
        return;
      }
      if (!editor) {
        return;
      }

      slotsRef.current[slot] = { editor, ref };
    },
    [isValidSlot],
  );

  const unregister = useCallback(
    (slot) => {
      if (!isValidSlot(slot)) return;

      slotsRef.current[slot] = { editor: null, ref: null };

      // If this was the active slot, fall back to the other one or default
      if (activeSlotRef.current === slot) {
        const otherSlot = slot === SLOTS.CHANNEL ? SLOTS.AGENT_CHAT : SLOTS.CHANNEL;
        if (slotsRef.current[otherSlot]?.editor) {
          activeSlotRef.current = otherSlot;
        } else {
          activeSlotRef.current = DEFAULT_SLOT;
        }
      }

      // Clear visibility restoration target if it was this slot
      if (focusTargetBeforeHideSlotRef.current === slot) {
        focusTargetBeforeHideSlotRef.current = null;
      }
    },
    [isValidSlot],
  );

  // Internal focus function
  const focusComposer = useCallback(
    (slot) => {
      if (!isValidSlot(slot)) {
        return;
      }

      const registeredEditor = slotsRef.current[slot]?.editor;

      if (!registeredEditor) {
        return;
      }

      activeSlotRef.current = slot;

      if (isEditorViewMounted(registeredEditor)) {
        registeredEditor.commands.focus('end');
        return;
      }

      // Registered before EditorContent attached the view (modal bootstrap):
      // focusing now throws, so wait for tiptap's mount event.
      registeredEditor.once('mount', function focusComposerOnMount() {
        registeredEditor.commands.focus('end');
      });
    },
    [isValidSlot],
  );

  // Named convenience methods for external use
  const focusChannelComposer = useCallback(() => focusComposer(SLOTS.CHANNEL), [focusComposer]);
  const focusAgentChatComposer = useCallback(() => focusComposer(SLOTS.AGENT_CHAT), [focusComposer]);

  // Check if agent chat is the active slot (for Escape key handling, etc.)
  const isAgentChatActive = useCallback(() => activeSlotRef.current === SLOTS.AGENT_CHAT, []);

  // Called when a composer naturally receives focus (user click, etc.)
  const setActiveComposer = useCallback(
    (slot) => {
      if (!isValidSlot(slot)) return;
      if (slotsRef.current[slot]?.editor) {
        activeSlotRef.current = slot;
      }
    },
    [isValidSlot],
  );

  // Set active composer without focusing (for click-to-route-keystrokes)
  const setChannelComposerActive = useCallback(() => setActiveComposer(SLOTS.CHANNEL), [setActiveComposer]);
  const setAgentChatComposerActive = useCallback(() => setActiveComposer(SLOTS.AGENT_CHAT), [setActiveComposer]);

  // Check if an editor is one of our managed editors
  const isManagedEditor = useCallback((activeEl) => {
    const chatEditor = slotsRef.current[SLOTS.CHANNEL]?.editor;
    const agentEditor = slotsRef.current[SLOTS.AGENT_CHAT]?.editor;

    // TipTap editors use a contenteditable element inside their container
    // Check if the active element is within either editor's DOM
    if (chatEditor?.options?.element?.contains(activeEl)) return true;
    if (agentEditor?.options?.element?.contains(activeEl)) return true;

    return false;
  }, []);

  // Routing is enabled either when the base focus router owns keyboard priority,
  // or when a modal owns priority AND has an embedded composer registered to
  // receive the keys. The modal case is gated on registration so we do not fall
  // back into the background channel composer while the modal is still bootstrapping.
  const canRouteComposerKeys = useCallback(() => {
    if (unlocked(KEYCHAIN.RICHDOWN_COMPOSER__FOCUS_ROUTER)) return true;
    return !!getModalOverrideEditor();
  }, [unlocked, getModalOverrideEditor]);

  // Visibility/focus handling
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        // Check KeyMaster before processing - if a modal is open, don't steal focus
        if (!canRouteComposerKeys()) {
          return;
        }
        setTimeout(() => focusBestTarget(), 0);
      } else {
        // Store which slot had focus
        focusTargetBeforeHideSlotRef.current = activeSlotRef.current;
      }
    };

    const handleWindowFocus = () => {
      // Check KeyMaster before processing - if a modal is open, don't steal focus
      if (!canRouteComposerKeys()) {
        return;
      }
      setTimeout(() => focusBestTarget(), 0);
    };

    const handleWindowBlur = () => {
      focusTargetBeforeHideSlotRef.current = activeSlotRef.current;
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('focus', handleWindowFocus);
    window.addEventListener('blur', handleWindowBlur);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', handleWindowFocus);
      window.removeEventListener('blur', handleWindowBlur);
    };
  }, [canRouteComposerKeys, focusBestTarget]);

  // Global keydown/paste handlers with KeyMaster integration
  useEffect(() => {
    const handleGlobalKeyDown = (event) => {
      // Check KeyMaster before processing - if a modal is open, let it handle keys
      if (!canRouteComposerKeys()) {
        return;
      }

      const activeEl = document.activeElement;

      // Skip if already focused on a managed editor
      if (isManagedEditor(activeEl)) {
        return;
      }

      // Skip if focused on any other input
      if (isInputElement(activeEl)) {
        return;
      }

      // Only route typing keys (not shortcuts, navigation keys, etc)
      if (!isTypingKey(event)) {
        return;
      }

      // Route to active editor (or default to chat)
      const target = getActiveEditor();

      if (target?.editor) {
        event.preventDefault();
        target.editor.commands.focus('end');
        // Only insert content if it's a typable key (not Enter, Backspace, etc)
        if (isTypableKey(event.key)) {
          target.editor.commands.insertContent(event.key);
        }
      }
    };

    const handleGlobalPaste = (event) => {
      // Check KeyMaster before processing
      if (!canRouteComposerKeys()) {
        return;
      }

      const activeEl = document.activeElement;

      // Skip if already focused on a managed editor
      if (isManagedEditor(activeEl)) {
        return;
      }

      // Skip if focused on any other input
      if (isInputElement(activeEl)) {
        return;
      }

      // Route to active editor
      const target = getActiveEditor();

      if (target?.editor && target?.ref?.current) {
        event.preventDefault();
        target.ref.current.forcePaste(event);
      }
    };

    document.addEventListener('keydown', handleGlobalKeyDown);
    document.addEventListener('paste', handleGlobalPaste);

    // Register as base layer with KeyMaster
    kmRegister(KEYCHAIN.RICHDOWN_COMPOSER__FOCUS_ROUTER);

    return () => {
      document.removeEventListener('keydown', handleGlobalKeyDown);
      document.removeEventListener('paste', handleGlobalPaste);
      kmUnregister(KEYCHAIN.RICHDOWN_COMPOSER__FOCUS_ROUTER);
    };
  }, [canRouteComposerKeys, kmRegister, kmUnregister, getActiveEditor, isManagedEditor, isInputElement, isTypingKey, isTypableKey]);

  const value = {
    // Registration (used by RichdownComposer)
    register,
    unregister,
    setActiveComposer,

    // Named convenience methods for external use
    focusChannelComposer,
    focusAgentChatComposer,
    setChannelComposerActive,
    setAgentChatComposerActive,
    isAgentChatActive,
  };

  return <RichdownComposerFocusRouterContext.Provider value={value}>{children}</RichdownComposerFocusRouterContext.Provider>;
}

export function useRichdownComposerFocusRouter() {
  const context = useContext(RichdownComposerFocusRouterContext);
  if (!context) {
    throw new Error('useRichdownComposerFocusRouter must be used within a RichdownComposerFocusRouterProvider');
  }
  return context;
}
